import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompetition } from "@/context/CompetitionContext";
import CompetitionSelector from "@/components/CompetitionSelector";
import {
  Users,
  Tags,
  ClipboardCheck,
  Gavel,
  CheckCircle2,
  Database,
  FileSpreadsheet,
  Clock,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

const fmtSize = (b) => {
  if (b == null) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { current, currentId } = useCompetition();
  const [stats, setStats] = useState(null);
  const [lastBackup, setLastBackup] = useState(null);
  const [nextSched, setNextSched] = useState("");
  const [exporting, setExporting] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!currentId) return;
    api.get(`/stats/${currentId}`).then((r) => setStats(r.data)).catch(() => {});
  }, [currentId]);

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/admin/backup/last")
      .then((r) => {
        setLastBackup(r.data.last || null);
        setNextSched(r.data.next_scheduled || "");
      })
      .catch(() => {});
  }, [isAdmin]);

  const exportExcel = async () => {
    setExporting(true);
    setMsg("");
    try {
      const res = await fetch(`${BACKEND}/api/admin/export/excel`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Falha ao exportar (" + res.status + ")");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = (m && m[1]) || "fits4you_export.xlsx";
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setMsg("✓ Excel gerado: " + filename);
    } catch (e) {
      setMsg("Erro: " + e.message);
    } finally {
      setExporting(false);
    }
  };

  const cards = [
    { label: "Atletas Inscritos", value: stats?.total_athletes ?? "—", icon: Users },
    { label: "Categorias", value: stats?.total_categories ?? "—", icon: Tags },
    { label: "Súmulas Lançadas", value: stats?.total_scores ?? "—", icon: ClipboardCheck },
    { label: "Árbitros Ativos", value: stats?.total_judges ?? "—", icon: Gavel },
    { label: "Categorias com Notas", value: stats?.categories_completed ?? "—", icon: CheckCircle2 },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">Dashboard</div>
          <h1 className="font-display text-5xl font-black tracking-tight leading-none">
            COMANDO
            <br />
            <span className="text-[var(--text-secondary)]">DO EVENTO</span>
          </h1>
        </div>
        <CompetitionSelector />
      </div>

      {isAdmin && (
        <div
          data-testid="backup-widget"
          className="card p-5 mb-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center border-l-4 border-[#5AF78E]"
        >
          <div className="flex items-start gap-3">
            <Database size={24} className="text-[#5AF78E] shrink-0 mt-1" />
            <div>
              <div className="overline mb-1">Última cópia de segurança</div>
              {lastBackup ? (
                <div>
                  <div
                    data-testid="last-backup-date"
                    className="font-display font-black text-xl tracking-tight"
                  >
                    {fmtDT(lastBackup.created_at)}
                  </div>
                  <div className="text-xs text-[var(--text-secondary)] mt-1">
                    <span className="font-mono">{lastBackup.filename}</span> ·{" "}
                    {fmtSize(lastBackup.size_bytes)}
                  </div>
                </div>
              ) : (
                <div
                  data-testid="last-backup-empty"
                  className="text-[#FFCC00] font-semibold"
                >
                  Nenhum backup ainda — crie um agora
                </div>
              )}
              {nextSched && (
                <div className="text-xs text-[var(--text-secondary)] mt-2 flex items-center gap-1">
                  <Clock size={11} /> Próximo automático: {fmtDT(nextSched)}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 md:items-end">
            <button
              data-testid="dashboard-export-excel"
              className="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
              onClick={exportExcel}
              disabled={exporting}
            >
              <FileSpreadsheet size={14} />
              {exporting ? "Gerando..." : "EXPORTAR BASE PARA EXCEL"}
            </button>
            <a
              href="/backup"
              data-testid="dashboard-goto-backup"
              className="text-xs text-[var(--text-secondary)] underline"
            >
              Gerenciar backups →
            </a>
          </div>
        </div>
      )}

      {isAdmin && msg && (
        <div
          data-testid="dashboard-export-msg"
          className={
            "text-xs px-3 py-2 border mb-4 " +
            (msg.startsWith("Erro")
              ? "text-[#C9A84C] border-[#C9A84C]/30 bg-[#C9A84C]/10"
              : "text-[#5AF78E] border-[#5AF78E]/30 bg-[#5AF78E]/10")
          }
        >
          {msg}
        </div>
      )}

      {current && (
        <div className="card p-6 mb-6">
          <div className="overline mb-2">Competição atual</div>
          <div className="font-display text-3xl font-black tracking-tight">
            {current.nome}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-4 text-sm">
            <div>
              <div className="overline mb-1">Data</div>
              <div className="tabular-nums">{current.data}</div>
            </div>
            <div>
              <div className="overline mb-1">Local</div>
              <div>{current.local || "—"}</div>
            </div>
            <div>
              <div className="overline mb-1">Promotor</div>
              <div>{current.promotor || "—"}</div>
            </div>
            <div>
              <div className="overline mb-1">Árbitros</div>
              <div className="tabular-nums">{current.num_arbitros}</div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="stat-card" data-testid={`stat-${c.label}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="overline">{c.label}</div>
              <c.icon size={18} className="text-[var(--text-secondary)]" />
            </div>
            <div className="stat-number">{c.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
